import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import ResetPassword from './pages/ResetPassword';
import LocationEquipment from './pages/LocationEquipment';
import LocationMaintenanceLogs from './pages/LocationMaintenanceLogs';
import LocationMaintenanceRequests from './pages/LocationMaintenanceRequests';
import MaintenanceHistory from './pages/MaintenanceHistory';
import MachineryDetail from './pages/MachineryDetail';
import WorkOrderPrintView from './pages/WorkOrderPrintView';
import Inventory from './pages/Inventory';
import SerializedComponentDetail from './pages/SerializedComponentDetail';
import Analytics from './pages/Analytics';
import { Toaster } from './components/ui/sonner';
import { PinsProvider } from './lib/PinsContext';
import './App.css';

// When REACT_APP_BACKEND_URL is unset (or empty), API becomes "/api" -- a
// relative URL that always inherits the page's protocol + host. This is the
// recommended setup for production deployments where the frontend is served
// from the same origin as the backend (e.g. via Caddy reverse proxy). It
// makes "wrong scheme" mistakes (http vs https) impossible.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// On any 401 from the backend, the token's expired or invalid. Drop it and
// send the user back to /login instead of letting them stare at a toast.
// Skip if the user is *already* on /login (avoids reload-loops on a bad
// password attempt — those 401s should surface as a normal error).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const onLogin = window.location.pathname.startsWith('/login') || window.location.pathname.startsWith('/reset-password');
      if (!onLogin) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <PinsProvider key={user?.id || 'signed-out'} userId={user?.id}>
        <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/location/:locationId"
            element={
              user ? (
                <LocationEquipment user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              user ? (
                <Users user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/location/:locationId/maintenance-logs"
            element={
              user ? (
                <LocationMaintenanceLogs user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/location/:locationId/maintenance-requests"
            element={
              user ? (
                <LocationMaintenanceRequests user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/machinery/:id"
            element={
              user ? (
                <MachineryDetail user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/inventory"
            element={
              user ? (
                <Inventory user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/work-orders/:id/print" element={user ? <WorkOrderPrintView /> : <Navigate to="/login" replace />} />
          <Route
            path="/inventory/serialized/:id"
            element={
              user ? (
                <SerializedComponentDetail user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/maintenance-history"
            element={
              user ? (
                <MaintenanceHistory user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/analytics"
            element={
              user ? (
                <Analytics user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      </PinsProvider>
      <Toaster />
    </div>
  );
}

export default App;
